import { saveAs } from "file-saver";
import { Packer } from "docx";
import { DocumentCreator } from "./createWordDoc";

/**
 * Converts a Blob object to a data URL.
 * @param {Blob} blob - The blob to convert to a data URL.
 * @returns {Promise<string>} A promise that resolves with the data URL
 * representation of the blob.
 */

export function blobToURL(blob) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onloadend = function () {
      const base64data = reader.result;
      resolve(base64data);
    };
  });
}

/**
 * Converts a base64 data URL to a Blob object.
 * @param {string} base64data - The base64 data URL to convert to a Blob.
 * @param {string} [contentType=application/pdf] - The MIME type of the Blob.
 * @returns {Promise<Blob>} A promise that resolves with the Blob representation
 * of the base64 data URL.
 */
export async function base64ToBlob(base64data, contentType = 'application/pdf') {
  const base64Response = await fetch(`data:${contentType};base64,${base64data}`);
  const fileBlob = await base64Response.blob();
  return fileBlob;
}

/**
 * Converts a Blob object to a data URL string.
 * This function creates a URL representing the Blob object that can be used in, for example, an <img> element.
 * 
 * @param {Blob} blob - The blob to convert to a data URL string.
 * @returns {string} The data URL string representing the Blob object.
 */

export function blobToDataURL(blob) {
  return URL.createObjectURL(blob);
}

/**
 * Downloads a file from a Blob object and saves it with the given filename.
 * @param {Blob} blob - The blob to download.
 * @param {string} fileName - The filename to save the blob as.
 */
export function downloadBlobFile(blob, fileName) {
  const fileURL = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = fileURL;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Downloads a file from the given URI and saves it with the given filename.
 * @param {string} uri - The URI of the file to be downloaded.
 * @param {string} fileName - The filename to save the downloaded file as.
 */
export function downloadFromURI(uri, fileName) {
  const link = document.createElement('a');
  link.href = uri;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Converts a File object to a Blob object.
 * @param {File} file - The file to convert.
 * @returns {Promise<Blob>} A promise that resolves with the Blob representation of the File object.
 */
export async function fileToBlob(file) {
  return new Promise((resolve) => {
    const { content, size } = file;
    let chunks = [];
    let i = 0;
    const totalCount = Math.round(size / 250000);

    for (const chunk of content) {
      chunks.push(chunk);
      i++;
    }
    return resolve(new Blob(chunks));
  });
};

/**
 * Copies the given text to the system clipboard.
 * @param {string} text - The text to copy to the clipboard.
 */
export function copyToClipboard(text) {
  // Create a text area element
  const textArea = document.createElement('textarea');

  // Set the value of the text area to the text you want to copy
  textArea.value = text;

  // Append the text area to the document
  document.body.appendChild(textArea);

  // Select the text in the text area
  textArea.select();

  try {
    // Execute the 'copy' command to copy the selected text to the clipboard
    document.execCommand('copy');
    console.log('Text copied to clipboard:', text);
  } catch (err) {
    console.error('Unable to copy text to clipboard:', err);
  } finally {
    // Remove the text area from the document
    document.body.removeChild(textArea);
  }
}

/**
 * Returns a string representing the time difference between the given date and the current date.
 * @param {Date|string} pastDate - The date in the past to compare with the current date.
 * @param {string} type - The type of the event, e.g. 'Posted', 'Updated', 'Created', etc.
 * @returns {string} A string representing the time difference, e.g. 'Updated 2 days ago'.
 */
export function getTimeDifferenceString(pastDate, type) {
  const currentDate = new Date();
  const timeDifference = Math.abs(currentDate - new Date(pastDate));
  const secondsDifference = Math.floor(timeDifference / 1000);
  const minutesDifference = Math.floor(secondsDifference / 60);
  const hoursDifference = Math.floor(minutesDifference / 60);
  const daysDifference = Math.floor(hoursDifference / 24);
  const monthsDifference = Math.floor(daysDifference / 30);
  const yearsDifference = Math.floor(monthsDifference / 12);
  if (yearsDifference > 0) {
    return `${type} ${yearsDifference} ${yearsDifference === 1 ? 'year' : 'years'} ago`;
  } else if (monthsDifference > 0) {
    return `${type} ${monthsDifference} ${monthsDifference === 1 ? 'month' : 'months'} ago`;
  } else if (daysDifference > 0) {
    return `${type} ${daysDifference} ${daysDifference === 1 ? 'day' : 'days'} ago`;
  } else if (hoursDifference > 0) {
    return `${type} ${hoursDifference} ${hoursDifference === 1 ? 'hr' : 'hrs'} ago`;
  } else if (minutesDifference > 0) {
    return `${type} ${minutesDifference} ${minutesDifference === 1 ? 'min' : 'mins'} ago`;
  } else {
    return `${type} just now`;
  }
};

/**
 * Converts a full name string into a formatted version with each word capitalized.
 * If the input is null or undefined, it returns the input as is.
 * 
 * @param {string} fullName - The full name to be formatted.
 * @returns {string} The formatted name with each word capitalized.
 */

export function changeNameFormat(fullName) {
  if (!fullName) return fullName;
  let words = fullName?.split(' ');
  let name = words.map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  return name;
};

/**
 * Format a given number in a human-readable format with units.
 * If the input is not a number, it returns "Not Disclosed".
 * @param {number} num - The number to be formatted.
 * @returns {string} The formatted string with units.
 */
export function formatNumber(num) {
  if (num >= 1e12) {
    return ` ${(num / 1e12).toFixed(1)} L Cr PA `;
  } else if (num >= 1e10) {
    return ` ${(num / 1e10).toFixed(1)} K Cr PA `;
  } else if (num >= 1e7) {
    return ` ${(num / 1e7).toFixed(1)} Cr PA `;
  } else if (num >= 1e5) {
    return ` ${(num / 1e5).toFixed(1)} LPA `;
  } else if (num >= 1e3) {
    return ` ${(num / 1e3).toFixed(1)} K PA `;
  }
  return num ? num.toString() : "Not Disclosed";
}

/**
 * Formats a given numeric value as a currency string in the Indian locale.
 *
 * @param {number} value - The numeric value to format as currency.
 * @returns {string} The formatted currency string with Indian locale comma separators.
 * Returns an empty string if the value is undefined, null, or not a number.
 */

export function formatCurrency(value) {
  if (value === undefined || value === null || isNaN(value)) return '';
  return Number(value).toLocaleString("en-IN");
};
/**
 * Converts a given string into a title cased string, with each word's first letter
 * capitalized and the rest of the word in lower case. If the string contains quoted
 * parts, they are left as is.
 *
 * @param {string} str - The string to be converted to title case.
 * @returns {string} The title cased string.
 */

export function toTitleCase(str) {
  return str
    .split(/(\s+|".*?")/) // Split by spaces or quoted parts
    .map(segment => {
      // If the segment is a quoted part, return it as is
      if (segment.startsWith('"') && segment.endsWith('"')) {
        return segment;
      }
      // If the segment is a regular word, capitalize it
      return segment
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    })
    .join('');
};

/**
 * Converts a given string to upper case, useful for converting strings to
 * all capital letters. If the input is not a string, it will throw an error.
 *
 * @param {string} sentence - The string to be converted to all upper case.
 * @returns {string} The string in all upper case.
 */

export function allCapitalize(sentence) {
  if (typeof sentence !== 'string') {
    throw new Error('Input must be a string');
  }
  return sentence.toUpperCase();
}

/**
 * Downloads a resume as a Word document using the given data.
 *
 * @param {array} props - An array containing the user's profile picture, user object, and resume data
 * @returns {Promise<void>} - A promise that resolves when the download is complete.
 * @throws {Error} - If there is an error downloading the Word document.
 */
export const downloadWordDoc = async ([pic, user, data]) => {
  let blob = null;

  try {
    const response = await fetch(pic, { mode: "cors" });
    if (response.ok) {
      blob = await response.blob();
    } else {
      console.error("Failed to fetch image:", response.statusText);
      blob = new Blob();
    }
  } catch (error) {
    blob = new Blob();
  }

  const documentCreator = new DocumentCreator();
  const doc = documentCreator.create({ imageArrayBuffer: blob, ...data });

  const generatedBlob = await Packer.toBlob(await doc);
  saveAs(generatedBlob, `${user?.name || 'Word'}.docx`);
};

/**
 * Masks a given phone number by replacing the middle 5 digits with 'xxxxx'.
 * If the phone number is not 10 digits long, it is returned unmodified.
 *
 * @param {string|number} phoneNumber - The phone number to be masked.
 * @returns {string} The masked phone number.
 */
export const maskPhoneNumber = (phoneNumber) => {
  const strPhoneNumber = phoneNumber.toString();
  if (strPhoneNumber.length !== 10) return phoneNumber;
  return strPhoneNumber.slice(0, 3) + 'xxxxx' + strPhoneNumber.slice(-2);
};

/**
 * Masks an email address by hiding the middle of the local part with asterisks.
 * Leaves the domain part intact.
 *
 * @param {string} email - The email address to be masked.
 * @returns {string} The masked email address.
 * @throws {Error} - If the input is not a valid email address.
 */
export function maskEmail(email) {
  const [local, domain] = email.split("@");

  if (!local || !domain) {
    throw new Error("Invalid email format");
  }

  const maskedLocal = local.slice(0, 2) + "*".repeat(local.length - 2);

  // Combine masked local part with the intact domain
  return `${maskedLocal}@${domain}`;
}

export const reorderWorkHistory = (workHistory) => {
  if (workHistory?.length === 0) return [];
  const presentJobs = workHistory.filter(job => job.present);
  const otherJobs = workHistory.filter(job => !job.present);

  const maxWorkHistoryToJob = otherJobs.reduce((maxJob, job) => {
    const jobDate = new Date(job.work_history_to || 0);
    const maxJobDate = maxJob ? new Date(maxJob.work_history_to || 0) : new Date(0);
    return jobDate > maxJobDate ? job : maxJob;
  }, null);

  const remainingJobs = otherJobs.filter(job => job !== maxWorkHistoryToJob);

  remainingJobs.sort((a, b) => new Date(b.work_history_from) - new Date(a.work_history_from));

  return [
    ...presentJobs,
    ...(maxWorkHistoryToJob ? [maxWorkHistoryToJob] : []),
    ...remainingJobs,
  ];
};
